import { createElement, type ReactNode } from 'react';

const booleanAttributes = new Set(['hidden', 'disabled', 'checked', 'defaultChecked', 'selected', 'multiple', 'required', 'readOnly', 'autoFocus', 'open', 'controls']);
const svgAttributeNames: Record<string, string> = {
  'stroke-width': 'strokeWidth', 'stroke-linecap': 'strokeLinecap', 'stroke-linejoin': 'strokeLinejoin',
  'fill-rule': 'fillRule', 'clip-rule': 'clipRule', 'stop-color': 'stopColor', 'stop-opacity': 'stopOpacity',
  'text-anchor': 'textAnchor', 'dominant-baseline': 'dominantBaseline', 'preserveAspectRatio': 'preserveAspectRatio',
  'font-size': 'fontSize', 'aria-hidden': 'aria-hidden', 'viewbox': 'viewBox',
};

function parseStyle(input: string): Record<string, string | number> {
  return Object.fromEntries(input.split(';').flatMap((declaration) => {
    const split = declaration.indexOf(':');
    if (split < 0) return [];
    const rawName = declaration.slice(0, split).trim();
    const name = rawName.startsWith('--') ? rawName : rawName.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
    return [[name, declaration.slice(split + 1).trim()]];
  }));
}

/** Converts the locally maintained, script-free POC HTML into React elements. */
export function toReact(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    const parentTag = node.parentElement?.tagName;
    if (/^\s*$/.test(node.textContent ?? '') && ['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR'].includes(parentTag ?? '')) return null;
    return node.textContent;
  }
  if (!(node instanceof HTMLElement || node instanceof SVGElement)) return null;
  const tag = node.tagName.toLowerCase();
  if (['script', 'style', 'link', 'meta', 'title', 'base', 'iframe', 'object', 'embed'].includes(tag)) return null;
  const props: Record<string, unknown> = { key };
  for (const attribute of Array.from(node.attributes)) {
    const sourceName = attribute.name.toLowerCase();
    if (sourceName.startsWith('on') || sourceName === 'srcdoc') continue;
    let name = attribute.name === 'class' ? 'className' : attribute.name === 'for' ? 'htmlFor' : sourceName === 'autocomplete' ? 'autoComplete' : (svgAttributeNames[sourceName] ?? attribute.name);
    if (name === 'style') { props.style = parseStyle(attribute.value); continue; }
    if (name === 'checked') name = 'defaultChecked';
    if (booleanAttributes.has(name)) { if (attribute.value !== 'false') props[name] = true; continue; }
    if (name === 'value' && (tag === 'input' || tag === 'textarea')) name = 'defaultValue';
    props[name] = attribute.value;
  }
  if (tag === 'textarea' && props.defaultValue === undefined) props.defaultValue = node.textContent ?? '';
  const children = tag === 'textarea' ? [] : Array.from(node.childNodes).map((child, index) => toReact(child, `${key}.${index}`));
  return createElement(tag, props, ...children);
}

export interface ReferenceDocument {
  bodyClass: string;
  bodyPage: string;
  title: string;
  tree: ReactNode;
  scripts?: string[];
}

export function parseReference(markup: string): ReferenceDocument {
  const documentNode = new DOMParser().parseFromString(markup, 'text/html');
  // The sources are checked-in POC files. Still fail closed if scriptable content is introduced.
  for (const element of documentNode.querySelectorAll('script, iframe, object, embed, base')) element.remove();
  for (const element of documentNode.body.querySelectorAll('*')) {
    for (const attribute of Array.from(element.attributes)) {
      if (attribute.name.toLowerCase().startsWith('on') || /^(?:javascript|data):/i.test(attribute.value.trim())) element.removeAttribute(attribute.name);
    }
  }
  for (const mark of documentNode.querySelectorAll<HTMLImageElement>('img.brand-logo')) {
    mark.src = '/ARTCI_logo.svg'; mark.alt = 'ARTCI';
  }
  for (const mark of documentNode.querySelectorAll('.brand-mark, .command-mark')) {
    const logo = documentNode.createElement('img');
    logo.className = 'brand-logo'; logo.src = '/ARTCI_logo.svg'; logo.alt = 'ARTCI';
    mark.replaceWith(logo);
  }
  for (const mark of documentNode.querySelectorAll('.artci-logo')) {
    const logo = documentNode.createElement('img');
    logo.className = 'artci-logo artci-logo--lockup'; logo.src = '/ARTCI_logo.svg'; logo.alt = 'ARTCI — Autorité de Régulation des Télécommunications/TIC de Côte d’Ivoire';
    mark.replaceWith(logo);
  }
  for (const profile of documentNode.querySelectorAll<HTMLElement>('.command-profile, .profile-control, .profile-button')) {
    profile.setAttribute('aria-label', 'Se connecter');
    profile.dataset.login = 'true';
    profile.removeAttribute('data-profile-toggle');
    const avatar = profile.querySelector('.command-avatar, .avatar');
    if (avatar) avatar.textContent = 'MS';
    const name = profile.querySelector('.profile-name, .profile-label') ?? profile.children.item(1);
    if (name) name.textContent = 'Connexion';
    const profileContainer = profile.closest<HTMLElement>('.popover-wrap') ?? profile;
    const actions = profileContainer.parentElement;
    if (actions && !actions.querySelector(':scope > .hub-admin-link')) {
      const administration = documentNode.createElement('a');
      administration.className = 'hub-admin-link';
      administration.href = '/administration';
      administration.textContent = 'Administration';
      administration.setAttribute('aria-label', 'Ouvrir le centre d’administration');
      actions.insertBefore(administration, profileContainer);
    }
  }
  for (const panel of documentNode.querySelectorAll('[data-profile-panel]')) panel.remove();
  return {
    bodyClass: documentNode.body.className,
    bodyPage: documentNode.body.dataset.page ?? '',
    title: documentNode.title,
    tree: Array.from(documentNode.body.childNodes).map((node, index) => toReact(node, String(index))),
  };
}

export function referenceForPath(path: string): { file: string; css: string[]; scripts?: string[] } {
  if (path.endsWith('/direction/index.html')) return { file: 'direction/index.html', css: ['direction/styles.css'], scripts: ['direction/app.js'] };
  if (path === '/pilotage-directions') return { file: 'direction-pulse/index.html', css: ['direction-pulse/styles.css'] };
  if (path.endsWith('/direction-pulse/index.html')) return { file: 'direction-pulse/index.html', css: ['direction-pulse/styles.css'], scripts: ['direction-pulse/data.js', 'direction-pulse/app.js'] };
  if (path.endsWith('/pilotage-kpi/pilotage_kpi_dashboard_v8.html')) return { file: 'pilotage-kpi/pilotage_kpi_dashboard_v8.html', css: ['pilotage-kpi/styles.css'], scripts: ['pilotage-kpi/app.js'] };
  if (path.endsWith('/gouvernance-kpi/certification.html')) return { file: 'gouvernance-kpi/certification.html', css: ['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css'] };
  if (path.endsWith('/gouvernance-kpi/catalogue.html')) return { file: 'gouvernance-kpi/catalogue.html', css: ['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css'] };
  if (path.endsWith('/gouvernance-kpi/indicateur.html')) return { file: 'gouvernance-kpi/indicateur.html', css: ['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css'] };
  if (path.endsWith('/gouvernance-kpi/import-kpi.html')) return { file: 'gouvernance-kpi/import-kpi.html', css: ['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css'] };
  if (path.endsWith('/gouvernance-kpi/signals.html')) return { file: 'gouvernance-kpi/signals.html', css: ['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css'] };
  if (path.endsWith('/gouvernance-kpi/dashboard.html')) return { file: 'gouvernance-kpi/dashboard.html', css: ['gouvernance-kpi/styles.css', 'gouvernance-kpi/cockpit.css'] };
  return { file: 'index.html', css: ['portal.css'] };
}
